// src/components/AccountModal.jsx
// Gestion du compte : info, déconnexion (avec confirmation), suppression de compte (RGPD).

import React, { useState } from "react";
import { supabase } from "../supabase";

export function AccountModal({ user, onClose, onShowPrivacy, syncing = false }) {
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0 caché, 1 avertissement, 2 saisie
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleLogout = async () => {
    await supabase?.auth.signOut(); // déclenche le nettoyage du cache via onAuthStateChange
    onClose?.();
  };

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "SUPPRIMER") {
      setError('Tape "SUPPRIMER" pour confirmer.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("Session introuvable, reconnecte-toi puis réessaie.");

      const resp = await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);

      await supabase.auth.signOut();
      onClose?.();
      window.alert("Ton compte et toutes tes données ont été supprimés.");
    } catch (e) {
      console.error("[AccountModal] delete error:", e);
      setError(`Échec de la suppression : ${e.message || "inconnue"}`);
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={modal}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>👤 Mon compte</h2>
          <button onClick={onClose} style={closeBtn} aria-label="Fermer">✕</button>
        </div>

        <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 4 }}>Connecté en tant que</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#f1f5f9", marginBottom: 10, wordBreak: "break-all" }}>{user?.email}</div>

        {syncing ? (
          <div style={{ fontSize: 12, color: "#a78bfa", marginBottom: 22, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 9, height: 9, border: "2px solid rgba(167,139,250,0.3)", borderTopColor: "#a78bfa", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
            Synchronisation en cours…
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 22 }}>✓ Synchronisé</div>
        )}

        {!confirmingLogout ? (
          <button onClick={() => setConfirmingLogout(true)} style={btnNeutral}>Se déconnecter</button>
        ) : (
          <div style={confirmBox}>
            <div style={{ fontSize: 13, color: "#d1d5db", marginBottom: 10 }}>Confirmer la déconnexion ?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmingLogout(false)} style={{ ...btnNeutral, flex: 1 }}>Annuler</button>
              <button onClick={handleLogout} style={{ ...btnPrimary, flex: 1 }}>Se déconnecter</button>
            </div>
          </div>
        )}

        <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "22px 0" }} />

        <div style={{ fontSize: 11, color: "#f87171", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          Zone dangereuse
        </div>

        {deleteStep === 0 && (
          <button onClick={() => setDeleteStep(1)} style={btnDangerOutline}>🗑 Supprimer mon compte</button>
        )}

        {deleteStep === 1 && (
          <div style={dangerBox}>
            <div style={{ fontSize: 13, color: "#fca5a5", lineHeight: 1.5, marginBottom: 12 }}>
              Cette action est <b>irréversible</b>. Toutes tes annonces, budgets, documents et analyses seront définitivement supprimés, ainsi que ton compte.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setDeleteStep(0)} style={{ ...btnNeutral, flex: 1 }}>Annuler</button>
              <button onClick={() => setDeleteStep(2)} style={{ ...btnDanger, flex: 1 }}>Continuer</button>
            </div>
          </div>
        )}

        {deleteStep === 2 && (
          <div style={dangerBox}>
            <div style={{ fontSize: 13, color: "#fca5a5", marginBottom: 10 }}>
              Tape <b>SUPPRIMER</b> ci-dessous pour confirmer définitivement.
            </div>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SUPPRIMER" style={input} autoFocus />
            {error && <div style={{ color: "#fca5a5", fontSize: 12, marginTop: 8 }}>⚠ {error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => { setDeleteStep(0); setConfirmText(""); setError(null); }} style={{ ...btnNeutral, flex: 1 }} disabled={busy}>Annuler</button>
              <button onClick={handleDelete} style={{ ...btnDanger, flex: 1 }} disabled={busy}>
                {busy ? "Suppression…" : "Supprimer définitivement"}
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 22, textAlign: "center" }}>
          <button onClick={onShowPrivacy} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>
            Politique de confidentialité
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 2700, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modal = { background: "#0f0f13", color: "#f1f5f9", borderRadius: 18, border: "1px solid rgba(255,255,255,0.12)", padding: 24, maxWidth: 420, width: "100%", maxHeight: "90vh", overflow: "auto" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 };
const closeBtn = { background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: "50%", fontSize: 14, cursor: "pointer" };
const confirmBox = { padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" };
const dangerBox = { padding: 14, borderRadius: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" };
const input = { width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#fff", outline: "none", boxSizing: "border-box", letterSpacing: "0.1em", fontWeight: 600 };
const btnNeutral = { width: "100%", padding: "11px 16px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "#d1d5db", fontWeight: 600, cursor: "pointer", fontSize: 13 };
const btnPrimary = { padding: "11px 16px", borderRadius: 9, border: "none", background: "#a78bfa", color: "#0f0f13", fontWeight: 700, cursor: "pointer", fontSize: 13 };
const btnDangerOutline = { width: "100%", padding: "11px 16px", borderRadius: 9, border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "#f87171", fontWeight: 600, cursor: "pointer", fontSize: 13 };
const btnDanger = { padding: "11px 16px", borderRadius: 9, border: "none", background: "#dc2626", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 };
